import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  MapPinIcon,
  ExternalLinkIcon,
  PlusIcon,
  UserIcon,
} from "lucide-react";
import EditSection from "./EditSection";
import { useGetMoveQuery } from "@/store/moves/apis";
import { useLocation, useParams } from "react-router-dom";
import { AddressType, Move as MoveType, InventoryItem, Mover } from "@/types";
import { toast } from "sonner";
import { Address } from "./Address";
import Header from "./Header-vertical";
import Actions from "./components/Actions";
import { Loading } from "@/components/Loading";
import RateCharges from "./components/RateCharges";
import Timeline from "./components/Timeline";
// import Summary from "./components/Summary";
// import Links from "./components/Links";
import Schedule from "./components/Schedule";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { cn, formatService } from "@/lib/utils";
import Payments from "./components/Payments";
import { Button } from "@/components/ui/button";
import { format, formatDistanceToNowStrict } from "date-fns";
import { parseISO } from "date-fns";
import { DialogTrigger, DialogContent, Dialog } from "@/components/ui/dialog";
import { getSelectedMover } from "@/store/mover/slice";

const generateMapUrl = (
  origin: AddressType,
  destination: AddressType,
  stops: AddressType[]
) => {
  const baseUrl = "https://www.google.com/maps/dir/";
  const coordinates = [
    `${origin.latitude},${origin.longitude}`,
    ...stops.map((stop) => `${stop.latitude},${stop.longitude}`),
    `${destination.latitude},${destination.longitude}`,
  ];

  return baseUrl + coordinates.join("/");
};

const Move: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const mover = useSelector(getSelectedMover);
  const currentUser = useSelector(
    (state: RootState) => state.movers.currentUser
  );

  const [page, setPage] = useState<"summary" | "inventory" | "photos">(
    "summary"
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSectionType, setEditingSectionType] = useState<
    | "customer_info"
    | "service"
    | "items"
    | "billing"
    | "notes"
    | "addresses"
    | null
  >(null);

  const {
    data: move,
    isLoading,
    error,
    refetch,
  } = useGetMoveQuery(id, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (location.state?.toast) {
      toast.success(location.state.toast);
    }
  }, [location]);

  if (isLoading) return <Loading />;
  if (error) return <div>Error loading move</div>;
  if (!move) return <div>Move not found</div>;
  if (!mover) return <div>Mover not found</div>;

  // const openEditModal = (
  //   sectionType:
  //     | "customer_info"
  //     | "service"
  //     | "items"
  //     | "billing"
  //     | "notes"
  //     | "addresses"
  // ) => {
  //   setEditingSectionType(sectionType);
  //   setIsEditModalOpen(true);
  // };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingSectionType(null);
  };

  const handlePageChange = (page: "summary" | "inventory" | "photos") => {
    setPage(page);
  };

  const renderPage = () => {
    if (page === "summary") {
      return <Summary move={move} refetchMove={refetch} mover={mover} />;
    } else if (page === "inventory") {
      return <Inventory move={move} />;
    } else if (page === "photos") {
      return <Photos move={move} />;
    }
  };

  if (!move.id) return <div>Move not found</div>;

  return (
    <div className="pt-0 mb-4 border-b border-black">
      {/* <div className="py-4 px-6 border-b border-black">
        <h1 className="text-2xl font-semibold">{move.customer_name}</h1>
      </div> */}
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="col-span-1 lg:col-span-2 lg:min-h-[calc(100vh-100px)] border-r border-black px-4 lg:bg-stone-50">
          <Header move={move} />
        </div>
        <div className="col-span-1 lg:col-span-7 border-r border-black">
          <div className="flex flex-row pt-2">
            <Button
              variant="link"
              className={cn(
                "text-base text-black",
                page === "summary" && "underline underline-offset-4"
              )}
              onClick={() => handlePageChange("summary")}
            >
              Summary
            </Button>
            <Button
              variant="link"
              className={cn(
                "text-base text-black",
                page === "inventory" && "underline underline-offset-4"
              )}
              onClick={() => handlePageChange("inventory")}
            >
              Inventory
            </Button>
            <Button
              variant="link"
              className={cn(
                "text-base text-black",
                page === "photos" && "underline underline-offset-4"
              )}
              onClick={() => handlePageChange("photos")}
            >
              Photos
            </Button>
          </div>
          {renderPage()}
        </div>

        <div className="col-span-1 lg:col-span-3">
          {(currentUser?.owner ||
            currentUser?.sales ||
            currentUser?.dispatcher) && (
            <div className="p-4">
              <Actions move={move} refetchMove={refetch} />
            </div>
          )}

          {/* <div>
            <Drawer>
              <DrawerTrigger asChild>
                <Button variant="outline">Resources</Button>
              </DrawerTrigger>
              <DrawerContent className="h-[80vh]">
                <div className="mt-4 px-8">
                  <ResourceAssignment serviceDate={move.service_date} />
                </div>
              </DrawerContent>
            </Drawer>
          </div> */}
        </div>
      </div>

      {isEditModalOpen && (
        <EditSection
          id={move.id}
          section={editingSectionType}
          onClose={closeEditModal}
        />
      )}
    </div>
  );
};

const formatRoomName = (room: string | null): string => {
  if (!room) return "Uncategorized";
  return room
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const Inventory = ({ move }: { move: MoveType }) => {
  const inventoryByRoom = useMemo(() => {
    const grouped: Record<string, InventoryItem[]> = {};

    move.inventory_items.forEach((item) => {
      const room = item.room_or_type || "Uncategorized";
      if (!grouped[room]) {
        grouped[room] = [];
      }
      grouped[room].push(item);
    });

    const sortedRooms = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

    sortedRooms.forEach((room) => {
      grouped[room].sort((a, b) => a.name.localeCompare(b.name));
    });

    return { rooms: sortedRooms, items: grouped };
  }, [move.inventory_items]);

  const totals = useMemo(() => {
    return move.inventory_items.reduce(
      (acc, item) => {
        acc.totalWeight += item.total_weight_lbs || 0;
        acc.totalVolume += item.total_volume_cuft || 0;
        return acc;
      },
      { totalWeight: 0, totalVolume: 0 }
    );
  }, [move.inventory_items]);

  return (
    <div className="px-4 py-2 space-y-6">
      <div className="p-4 bg-stone-50 border border-stone-200">
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-lg font-semibold">
          <span>
            {move.inventory_items.length}{" "}
            <span className="text-sm font-normal">items</span>
          </span>
          <span>
            {totals.totalVolume.toFixed(2)}{" "}
            <span className="text-sm font-normal">cuft</span>
          </span>
          <span>
            {totals.totalWeight.toFixed(2)}{" "}
            <span className="text-sm font-normal">lbs</span>
          </span>
        </div>
      </div>

      {inventoryByRoom.rooms.map((room) => (
        <div key={room}>
          <h3 className="text-md font-semibold mb-3 pb-1 border-b border-stone-300 text-stone-700">
            {formatRoomName(room)}
          </h3>
          <ul className="space-y-2">
            {inventoryByRoom.items[room].map((item) => (
              <li
                key={item.id}
                className="justify-between items-center text-sm border-b border-dashed border-stone-200 pb-1 grid grid-cols-12 gap-2"
              >
                <span className="col-span-7 lg:col-span-4">{item.name}</span>
                <span className="col-span-5 lg:col-span-1">
                  {item.quantity}
                </span>
                <span className="hidden lg:block col-span-3 text-stone-600">
                  added{" "}
                  {formatDistanceToNowStrict(parseISO(item.created_at), {
                    addSuffix: true,
                  })}
                </span>
                <span className="hidden lg:block col-span-3 text-stone-600">
                  updated{" "}
                  {formatDistanceToNowStrict(parseISO(item.updated_at), {
                    addSuffix: true,
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {move.inventory_items.length === 0 && (
        <div className="text-center text-stone-500 py-4">
          No inventory items added yet.
        </div>
      )}
    </div>
  );
};

const Summary = ({
  move,
  refetchMove,
  mover,
}: {
  move: MoveType;
  refetchMove: () => void;
  mover: Mover | null;
}) => {
  const [isOfflinePaymentSheetOpen, setIsOfflinePaymentSheetOpen] =
    useState(false);

  if (!mover) return;

  return (
    <>
      <div className="border-b border-black">
        {/* <h3 className="text-base font-semibold px-4 py-1 bg-stone-100">
          Service
        </h3> */}
        <div className="px-4 py-2">
          <div className="flex flex-row justify-between items-center text-lg font-semibold">
            <h2 className="text-lg font-semibold">{formatService(move)}</h2>
            {move.inventory_items.length > 0 && (
              <div className="text-stone-700">
                {move.inventory_items.reduce(
                  (acc, item) => acc + item.total_volume_cuft,
                  0
                )}{" "}
                cuft{" / "}
                {move.inventory_items.reduce(
                  (acc, item) => acc + item.total_weight_lbs,
                  0
                )}{" "}
                lbs
              </div>
            )}
          </div>
          {move.notes_service && (
            <div className="mt-1">
              <p className="text-sm text-stone-900">{move.notes_service}</p>
            </div>
          )}
        </div>
      </div>
      <div className="border-b border-black">
        <h3 className="text-base font-semibold px-4 py-1 bg-stone-100">
          Schedule
        </h3>
        <div className="px-4 py-2">
          <Schedule move={move} />
        </div>
      </div>
      {move.onsite_events.length > 0 && (
        <div className="border-b border-black">
          <h3 className="text-base font-semibold px-4 py-1 bg-stone-100">
            Onsite Events
          </h3>
          <div className="divide-y divide-stone-200 divide-dashed">
            {move.onsite_events.map((event) => (
              <div className="px-4 py-2">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm space-y-1">
                      <div className="flex items-center gap-2">
                        <ClockIcon className="w-4 h-4" />
                        <span>
                          {format(parseISO(event.date), "MM/dd/yyyy")}
                          {" from "}
                          {event.start_time}
                          {" to "}
                          {event.end_time}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPinIcon className="w-4 h-4" />
                        <span>{event.address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4" />
                        <span>
                          {event.assigned_to?.first_name}{" "}
                          {event.assigned_to?.last_name}
                        </span>
                      </div>
                    </div>
                  </div>
                  {event.notes && (
                    <div>
                      <h4 className="text-sm font-semibold mb-1">Notes</h4>
                      <p className="text-sm text-stone-600">{event.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div id="addresses" className="border-b border-black">
        <h3 className="text-base font-semibold px-4 py-1 bg-stone-100">
          Locations
        </h3>
        <div
          className={`grid grid-cols-${
            1 + 1 + move.stops?.length
          } gap-4 px-4 py-2`}
        >
          <div className="col-span-1 w-full">
            {/* <div className="mb-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <div>-</div>
                  <span className="text-sm font-normal">
                    {move.waypoints_info.length > 0 &&
                      move.waypoints_info[1].distance.toFixed(1)}{' '}
                    mi (
                    {move.waypoints_info.length > 0 &&
                      minutesToHours(move.waypoints_info[1].duration)}
                    )
                  </span>
                  <div>-</div>
                </div> */}
            <div className="mb-2 flex flex-row items-center justify-beteween gap-2">
              <h3 className="text-sm font-semibold flex-grow">Origin</h3>
              <ChevronRightIcon className="w-4 h-4" />
            </div>
            {move.origin && <Address address={move.origin} />}
          </div>
          {move.stops.map((stop: AddressType, index: number) => (
            <div className="col-span-1" key={index}>
              <div className="mb-2 flex flex-row items-center justify-beteween gap-2">
                <h3 className="text-sm font-semibold flex-grow">
                  Stop #{index + 1}
                </h3>
                <ChevronRightIcon className="w-4 h-4" />
              </div>
              <Address address={stop} />
            </div>
          ))}
          <div className="col-span-1">
            <div className="mb-2 flex flex-row items-center justify-beteween gap-2">
              <h3 className="text-sm font-semibold flex-grow">Destination</h3>
            </div>
            {move.destination?.street && <Address address={move.destination} />}
          </div>

          {move.origin && move.destination?.street && (
            <div className="my-1">
              <a
                href={generateMapUrl(move.origin, move.destination, move.stops)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <ExternalLinkIcon className="w-4 h-4" />
                View in Map
              </a>
            </div>
          )}
        </div>
      </div>
      <div className="border-b border-black">
        <h3 className="text-base font-semibold px-4 py-1 bg-stone-100">
          {move.invoice?.id ? "Invoice" : "Rate and Fees"}
        </h3>
        <div className="px-4 py-2">
          <RateCharges move={move} mover={mover} />
        </div>
      </div>
      <div className="border-b border-black">
        <div className="flex items-center justify-between px-4 py-1 bg-stone-100">
          <h3 className="text-base font-semibold">Payments</h3>
          <button
            onClick={() => setIsOfflinePaymentSheetOpen(true)}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            <PlusIcon className="w-4 h-4" />
            Record Offline Payment
          </button>
        </div>
        <div className="px-4 py-2">
          <Payments
            move={move}
            refetchMove={refetchMove}
            isOfflinePaymentSheetOpen={isOfflinePaymentSheetOpen}
            setIsOfflinePaymentSheetOpen={setIsOfflinePaymentSheetOpen}
          />
        </div>
      </div>
      <div className="border-b border-black">
        <h3 className="text-base font-semibold px-4 py-1 bg-stone-100">
          Notes
        </h3>
        <div className="px-4 py-2 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="col-span-1">
            <h3 className="text-sm font-semibold">Customer Notes</h3>
            <p className="text-sm">{move.notes_customer || "-"}</p>
          </div>
          <div className="col-span-1">
            <h3 className="text-sm font-semibold">Internal Notes</h3>
            <p className="text-sm">{move.notes_internal || "-"}</p>
          </div>
          <div className="col-span-1">
            <h3 className="text-sm font-semibold">Personal Message</h3>
            <p
              className="text-sm"
              dangerouslySetInnerHTML={{
                __html: move.notes_personal_message || "-",
              }}
            />
          </div>
        </div>
      </div>
      <div className="">
        <h3 className="text-base font-semibold px-4 py-1 bg-stone-100">
          Timeline
        </h3>
        <div className="px-4 py-2">
          <Timeline move={move} />
        </div>
      </div>
    </>
  );
};

const Photos = ({ move }: { move: MoveType }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(
    null
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!dialogOpen || move.media.length <= 1 || selectedPhotoIndex === null)
        return;

      if (e.key === "ArrowRight") {
        // Navigate to next photo
        setSelectedPhotoIndex((selectedPhotoIndex + 1) % move.media.length);
        e.preventDefault();
      } else if (e.key === "ArrowLeft") {
        // Navigate to previous photo
        setSelectedPhotoIndex(
          (selectedPhotoIndex - 1 + move.media.length) % move.media.length
        );
        e.preventDefault();
      }
    },
    [dialogOpen, selectedPhotoIndex, move.media]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const navigateToNextPhoto = () => {
    if (selectedPhotoIndex !== null && move.media.length > 1) {
      setSelectedPhotoIndex((selectedPhotoIndex + 1) % move.media.length);
    }
  };

  const navigateToPrevPhoto = () => {
    if (selectedPhotoIndex !== null && move.media.length > 1) {
      setSelectedPhotoIndex(
        (selectedPhotoIndex - 1 + move.media.length) % move.media.length
      );
    }
  };

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
        {move.media.map((photo, index) => (
          <Dialog
            key={photo.id}
            open={dialogOpen && selectedPhotoIndex === index}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) setSelectedPhotoIndex(null);
            }}
          >
            <DialogTrigger asChild>
              <div
                className="relative group aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-200 max-w-[120px] cursor-pointer"
                onClick={() => setSelectedPhotoIndex(index)}
              >
                <img
                  src={photo.file_url}
                  alt="Move item"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity duration-200" />
              </div>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <div className="flex justify-center items-center relative">
                {move.media.length > 1 && (
                  <>
                    <button
                      onClick={navigateToPrevPhoto}
                      className="absolute left-0 z-10 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full m-2"
                      aria-label="Previous photo"
                    >
                      <ChevronLeftIcon className="w-6 h-6" />
                    </button>

                    <button
                      onClick={navigateToNextPhoto}
                      className="absolute right-0 z-10 bg-black/30 hover:bg-black/50 text-white p-2 rounded-full m-2"
                      aria-label="Next photo"
                    >
                      <ChevronRightIcon className="w-6 h-6" />
                    </button>
                  </>
                )}

                {selectedPhotoIndex !== null && (
                  <img
                    src={move.media[selectedPhotoIndex].file_url}
                    alt={move.media[selectedPhotoIndex].name || "Move item"}
                    className="max-h-[80vh] object-contain"
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </div>
  );
};

export default Move;
